import type {
    SketchDimensionTopologyChange,
    SketchDimensionTopologyChangePort,
    SketchDimensionPointTopologyMapping,
} from '../../../../types/sketch/dimension/SketchDimensionTopologyChangeTypes';
import {SketchDimensionReference} from '../model/SketchDimensionReference';
import type {SketchDimensionRepository} from '../repository/SketchDimensionRepository';
import type {SketchDimensionReferenceRecord, SketchDimensionSnapshot} from '../SketchDimensionTypes';

/**
 * 草图尺寸拓扑变化服务。
 *
 * 设计说明：
 * 1. 尺寸仓库只认识持久 ID，本服务只消费命令显式提供的持久拓扑映射；
 * 2. 存在唯一兼容继承实体时保留尺寸身份并重绑引用；
 * 3. 整体删除沿用 PuppyCAD 已确认的级联删除语义；
 * 4. 实体仍保留但特定尺寸语义失效时，只删除明确声明不兼容的尺寸类型；
 * 5. 重建型拓扑缺少确定点映射时进入 dangling，禁止按空间最近对象猜测；
 * 6. 本服务不理解 Trim、圆角或镜像命令，后续其它拓扑命令可复用同一入口。
 */
export class SketchDimensionTopologyChangeService implements SketchDimensionTopologyChangePort {
    /**
     * 应用一组确定性拓扑变化。
     *
     * @param repository 尺寸领域仓库。
     * @param sketchId 当前草图持久 ID。
     * @param changes 本次命令产生的持久拓扑变化。
     */
    public apply(
        repository: SketchDimensionRepository,
        sketchId: string,
        changes: readonly SketchDimensionTopologyChange[],
    ): void {
        if (changes.length === 0) {
            return;
        }

        // 同一源实体在一笔命令内只允许存在一个最终拓扑结果，后写入项表达最终状态。
        const changeBySourceEntityId = new Map<string, SketchDimensionTopologyChange>();

        changes.forEach((change) => {
            changeBySourceEntityId.set(change.sourceEntityId, change);
        });

        // 使用快照列表遍历，避免删除仓库项时破坏当前迭代顺序。
        const dimensionSnapshots = repository.listBySketch(sketchId)
            .map((dimension) => dimension.toSnapshot());

        dimensionSnapshots.forEach((snapshot) => {
            this.applyDimensionChange(repository, sketchId, snapshot, changeBySourceEntityId);
        });
    }

    /**
     * 对单个尺寸应用拓扑变化。
     *
     * @param repository 尺寸领域仓库。
     * @param sketchId 当前草图持久 ID。
     * @param snapshot 尺寸变更前快照。
     * @param changeBySourceEntityId 源实体到拓扑变化的映射。
     */
    private applyDimensionChange(
        repository: SketchDimensionRepository,
        sketchId: string,
        snapshot: SketchDimensionSnapshot,
        changeBySourceEntityId: ReadonlyMap<string, SketchDimensionTopologyChange>,
    ): void {
        let shouldDelete = false;
        let shouldMarkDangling = false;
        let referenceChanged = false;
        const nextReferences: SketchDimensionReferenceRecord[] = [];

        snapshot.references.forEach((reference) => {
            const change = changeBySourceEntityId.get(reference.entityId);

            if (!change) {
                nextReferences.push({...reference});
                return;
            }

            if (change.invalidatedDimensionKinds?.includes(snapshot.dimensionKind)) {
                // 当前实体仍可承载其它点/方向语义，但该尺寸类型已无法表达裁剪后的几何，必须删除整个尺寸聚合。
                shouldDelete = true;
                nextReferences.push({...reference});
                return;
            }

            // 源实体没有兼容继承目标时，尺寸定义已不完整，复合尺寸同样整体删除。
            if (change.targetEntityId === null) {
                shouldDelete = true;
                nextReferences.push({...reference});
                return;
            }

            const resolvedReference = this.resolveReferenceAfterTopologyChange(reference, change);

            if (!resolvedReference) {
                shouldMarkDangling = true;
                nextReferences.push({...reference});
                return;
            }

            referenceChanged = referenceChanged
                || JSON.stringify(reference) !== JSON.stringify(resolvedReference);
            nextReferences.push(resolvedReference);
        });

        if (shouldDelete) {
            repository.delete(sketchId, snapshot.dimensionId);
            return;
        }

        if (shouldMarkDangling) {
            // 已悬空尺寸保留最早修复签名，避免连续拓扑命令覆盖用户可见的原始引用。
            if (snapshot.solveState !== 'dangling') {
                repository.mutate(sketchId, snapshot.dimensionId, (dimension) => {
                    dimension.markDangling();
                });
            }
            return;
        }

        if (!referenceChanged || snapshot.solveState === 'dangling') {
            return;
        }

        // Reattach 排除类型与只读尺寸不能自动重绑；这些情况保留原引用并进入悬空修复流程。
        if (
            snapshot.readOnly
            || snapshot.dimensionKind === 'path-length'
            || snapshot.dimensionKind === 'spline-length'
        ) {
            repository.mutate(sketchId, snapshot.dimensionId, (dimension) => {
                dimension.markDangling();
            });
            return;
        }

        repository.mutate(sketchId, snapshot.dimensionId, (dimension) => {
            dimension.reattach(nextReferences.map((reference) => new SketchDimensionReference(reference)));
        });
    }

    /**
     * 解析单条尺寸引用在拓扑变化后的确定目标。
     *
     * @param reference 原尺寸引用。
     * @param change 对应源实体的拓扑变化。
     * @returns 可确定的新引用；无法确定时返回 null。
     */
    private resolveReferenceAfterTopologyChange(
        reference: SketchDimensionReferenceRecord,
        change: SketchDimensionTopologyChange,
    ): SketchDimensionReferenceRecord | null {
        if (!reference.pointId) {
            return {
                ...reference,
                entityId: change.targetEntityId ?? reference.entityId,
            };
        }

        const pointMapping = change.pointMappings.find((mapping) => (
            mapping.sourcePointId === reference.pointId
        ));

        if (pointMapping) {
            return this.applyPointMapping(reference, pointMapping);
        }

        if (change.deletedPointIds.includes(reference.pointId)) {
            return null;
        }

        // 实体原位保留时，未声明变化的点引用继续有效；重建为新实体时必须显式提供点映射。
        if (change.targetEntityId === change.sourceEntityId) {
            return {...reference};
        }

        return null;
    }

    /**
     * 把确定的点拓扑映射写回尺寸引用。
     *
     * @param reference 原尺寸引用。
     * @param mapping 点拓扑映射。
     * @returns 更新后的尺寸引用。
     */
    private applyPointMapping(
        reference: SketchDimensionReferenceRecord,
        mapping: SketchDimensionPointTopologyMapping,
    ): SketchDimensionReferenceRecord {
        return {
            ...reference,
            entityId: mapping.targetEntityId,
            pointId: mapping.targetPointId,
        };
    }
}
